import {NEVER, Observable, of} from 'rxjs';
import {map} from 'rxjs/operators';

import {Injectable, inject} from '@angular/core';

import * as APIM from '@common/utils/dist/models/api-messages.js';
import * as UU from '@common/utils/dist/utils/user-utils-pl2.js';
import {
  WebsocketMessageProcessor,
  WSMessageProcessorInput,
  WSMessageProcessorOutput,
} from './websocket-message-processor';
import {WSMessageReceiverService} from '@services/ws-message-receiver.service';
import {PosthogWrapperService} from '@services/posthog-wrapper.service';
import {AgentMemoryStore} from '@stores/agent-memory.store';
import {RouteStore} from '@stores/route.store';
import {UserStore} from '@stores/user.store';
import {ProtoCard} from '@models/proto-card-types';

@Injectable()
export class AgentStreamMessageProcessor implements WebsocketMessageProcessor {
  private _wsReceiver = inject(WSMessageReceiverService);
  private _memoryStore = inject(AgentMemoryStore);
  private _posthog = inject(PosthogWrapperService);
  private _routeStore = inject(RouteStore);
  private _userStore = inject(UserStore);

  processMessage(
    params: WSMessageProcessorInput,
  ): Observable<WSMessageProcessorOutput> {
    const msg = params.msg;

    if (msg.int !== APIM.EventIntent.AgentStreamEvent) {
      return NEVER;
    }

    return this._wsReceiver
      .receive<APIM.EventData<APIM.AgentStreamChunk>>(msg)
      .pipe(
        map((data) => {
          const chunk = data.c[0];
          if (!chunk) return true;
          this._processChunk(chunk);
          return true;
        }),
      );
  }

  private _processChunk(chunk: APIM.AgentStreamChunk) {
    const sid = chunk.chatSessionId;
    const session = this._memoryStore.getSession(sid);
    if (!session) return;

    switch (chunk.eventType) {
      case 'message_start':
        this._toolNamesPerSession.set(sid, []);
        this._messageStartTimes.set(sid, Date.now());
        this._responseTextPerSession.set(sid, '');
        break;

      case 'content_block_delta':
        if (chunk.deltaType === 'text' && chunk.text) {
          this._appendText(sid, chunk.text);
          this._responseTextPerSession.set(
            sid,
            (this._responseTextPerSession.get(sid) ?? '') + chunk.text,
          );
        }
        if (chunk.deltaType === 'tool_use_input' && chunk.toolInput) {
          this._appendToolInput(sid, chunk.toolInput);
        }
        break;

      case 'content_block_start':
        if (chunk.blockType === 'tool_use' && chunk.toolName) {
          this._memoryStore.setToolStatus(sid, this._toolLabel(chunk.toolName));
          this._setCurrentTool(sid, chunk.toolName);
          const tools = this._toolNamesPerSession.get(sid) ?? [];
          tools.push(chunk.toolName);
          this._toolNamesPerSession.set(sid, tools);
        }
        break;

      case 'content_block_stop':
        this._flushToolResult(sid);
        this._memoryStore.setToolStatus(sid, null);
        break;

      case 'message_stop':
        this._completeStream(sid);
        break;

      case 'metadata':
        this._captureAiGeneration(sid, chunk, true);
        break;

      case 'card': {
        const card = chunk.card ? this._protoCard(chunk.card) : undefined;
        // An updated card replaces the one already shown with the same id
        if (card && !this._memoryStore.replaceProtoCard(sid, card)) {
          this._addCard(sid, card);
        }
        break;
      }

      case 'visualization':
        if (chunk.visualization) {
          this._addVisualizationCard(sid, {...chunk.visualization});
        }
        break;

      case 'visualization_check':
        if (chunk.visualizationCheck) {
          this._memoryStore.updateVisualizationCards(
            sid,
            chunk.visualizationCheck.key,
            {check: chunk.visualizationCheck.check, checkPending: false},
          );
        }
        break;

      case 'context_changed':
        if (chunk.agentContext) {
          this._memoryStore.setAgentContext(sid, chunk.agentContext);
        }
        // The server paused the turn; the chat panel resumes it with fresh runtime context
        if (chunk.resumeToolUseId) {
          this._memoryStore.setPendingResume(sid, chunk.resumeToolUseId);
        }
        break;

      case 'error':
        this._handleError(sid, chunk.message ?? 'An error occurred.');
        this._captureAiGeneration(sid, chunk, false, chunk.message);
        break;
    }
  }

  // Text accumulation — update the streaming assistant message

  private _appendText(sid: string, text: string) {
    const session = this._memoryStore.getSession(sid);
    if (!session) return;

    const streamingMsg = session.messages.find((m) => m.isStreaming);
    if (streamingMsg) {
      this._memoryStore.updateStreamingMessage(
        sid,
        streamingMsg.id,
        (streamingMsg.content ?? '') + text,
      );
    }
  }

  // Tool input accumulation — buffer until content_block_stop

  private _toolNamesPerSession = new Map<string, string[]>();
  private _messageStartTimes = new Map<string, number>();
  private _responseTextPerSession = new Map<string, string>();
  private _promptCapturedForSession = new Set<string>();

  // Several cards can arrive in the same millisecond, so ids also carry a counter
  private _cardCount = 0;
  private _toolInputBuffers = new Map<
    string,
    {toolName: string; input: string}
  >();

  private _setCurrentTool(sid: string, toolName: string) {
    this._toolInputBuffers.set(sid, {toolName, input: ''});
  }

  private _appendToolInput(sid: string, input: string) {
    const buf = this._toolInputBuffers.get(sid);
    if (buf) {
      buf.input += input;
    }
  }

  private _flushToolResult(sid: string) {
    const buf = this._toolInputBuffers.get(sid);
    if (!buf?.input) {
      this._toolInputBuffers.delete(sid);
      return;
    }

    try {
      const result = JSON.parse(buf.input);

      if (buf.toolName === 'createProtoCard') {
        const card = result as ProtoCard;
        const session = this._memoryStore.getSession(sid);
        const streamingMsg = session?.messages.find((m) => m.isStreaming);
        if (streamingMsg) {
          this._memoryStore.addProtoCard(sid, streamingMsg.id, card);
        }
      }
    } catch (e) {
      console.warn(
        '[AgentStreamMessageProcessor] Failed to parse tool result:',
        buf.toolName,
        e,
      );
    }

    this._toolInputBuffers.delete(sid);
  }

  private _completeStream(sid: string) {
    const session = this._memoryStore.getSession(sid);
    if (!session) return;

    const streamingMsg = session.messages.find((m) => m.isStreaming);
    if (streamingMsg) {
      // Extract visualization spec from tagged block before stripping
      this._extractVisualizationSpec(sid, streamingMsg);

      // Strip XML-style tool call tags the model sometimes outputs as text
      const cleaned = this._stripToolXml(streamingMsg.content ?? '');

      if (cleaned !== streamingMsg.content) {
        this._memoryStore.updateStreamingMessage(sid, streamingMsg.id, cleaned);
      }
      this._memoryStore.completeStreamingMessage(sid, streamingMsg.id);
    }
    this._memoryStore.setStreaming(sid, false);
    this._toolInputBuffers.delete(sid);
  }

  /**
   * Legacy path: a visualization embedded in the reply text. The agent now uses the
   * create_visualization tool, but older conversation history can still produce the tag.
   */
  private _extractVisualizationSpec(
    sid: string,
    streamingMsg: {id: string; content?: string},
  ) {
    const content = streamingMsg.content ?? '';
    const match = content.match(
      /<visualization-spec>([\s\S]*?)<\/visualization-spec>/,
    );
    if (!match) return;

    try {
      this._addVisualizationCard(
        sid,
        JSON.parse(match[1]) as Record<string, unknown>,
      );
    } catch (e) {
      console.warn(
        '[AgentStreamMessageProcessor] Failed to parse visualization spec from text:',
        e,
      );
      this._memoryStore.addMessage(sid, {
        id: `${Date.now()}-system`,
        role: 'system',
        content:
          'The visualization could not be read. Ask the assistant to try again.',
        isStreaming: false,
      });
    }
  }

  private _protoCard(card: APIM.AgentCard): ProtoCard {
    const cardId =
      card.cardId || `${card.kind}-${Date.now()}-${this._cardCount++}`;
    const display = {layout: 'full-width' as const, style: 'info' as const};
    switch (card.kind) {
      case 'multiple_choice':
        return {
          cardId,
          type: 'multiple-choice',
          title: card.title,
          data: {
            title: card.title,
            prompt: card.prompt,
            options: card.options,
            correct: card.correct,
            multiSelect: card.multiSelect,
            ...(card.hints ? {hints: card.hints} : {}),
          },
          display: {...display, style: 'question'},
        };
      case 'matching':
        return {
          cardId,
          type: 'matching',
          title: card.title,
          data: {title: card.title, scene: card.scene},
          display,
        };
      case 'collect_data':
        return {
          cardId,
          type: 'collect-data',
          title: card.title,
          data: {title: card.title, setup: card.setup},
          display,
        };
      case 'table':
        return {
          cardId,
          type: 'data-table',
          title: card.title,
          data: {title: card.title, columns: card.columns, rows: card.rows},
          display,
        };
      case 'free_response':
        return {
          cardId,
          type: 'free-response',
          title: card.title,
          data: {
            title: card.title,
            prompt: card.prompt,
            ...(card.hint ? {hint: card.hint} : {}),
          },
          display: {...display, style: 'question'},
        };
      default:
        return {
          cardId,
          type: 'text-section',
          title: card.title,
          data: {title: card.title, markdown: card.markdown},
          display,
        };
    }
  }

  private _addCard(sid: string, card: ProtoCard) {
    const session = this._memoryStore.getSession(sid);
    const streamingMsg = session?.messages.find((m) => m.isStreaming);
    if (streamingMsg) {
      this._memoryStore.addProtoCard(sid, streamingMsg.id, card);
    }
  }

  private _addVisualizationCard(sid: string, vizSpec: Record<string, unknown>) {
    const session = this._memoryStore.getSession(sid);
    const streamingMsg = session?.messages.find((m) => m.isStreaming);
    if (!streamingMsg) return;

    const errors = validateVisualizationSpec(vizSpec);
    const card: ProtoCard = {
      cardId: `viz-${Date.now()}-${this._cardCount++}`,
      type: 'visualization',
      title: errors.length ? 'Visualization failed' : 'Custom Visualization',
      data: {
        ...vizSpec,
        validationErrors: errors.length ? errors : undefined,
      },
      display: {
        layout: 'full-width',
        style: errors.length ? 'warning' : 'info',
      },
    };
    this._memoryStore.addProtoCard(sid, streamingMsg.id, card);
  }

  private _stripToolXml(text: string): string {
    return (
      text
        .replace(
          /<search_support_content>[\s\S]*?<\/search_support_content>/g,
          '',
        )
        .replace(/<visualization-spec>[\s\S]*?<\/visualization-spec>/g, '')
        .replace(/<agent-context>[\s\S]*?<\/agent-context>/g, '')
        // Tool calls the model wrote as text when the tools weren't available to it
        .replace(
          /<(create_visualization|update_visualization_request|update_experiment_plan|update_device_issue|update_explanation_request|focus_trial|set_user_reported_device|set_standards_framework)>[\s\S]*?(<\/\1>|$)/g,
          '',
        )
        .trim()
    );
  }

  private _handleError(sid: string, message: string) {
    const session = this._memoryStore.getSession(sid);
    if (!session) return;

    const streamingMsg = session.messages.find((m) => m.isStreaming);
    if (streamingMsg) {
      this._memoryStore.updateStreamingMessage(sid, streamingMsg.id, message);
      this._memoryStore.completeStreamingMessage(sid, streamingMsg.id);
    }
    this._memoryStore.setStreaming(sid, false);
    this._toolInputBuffers.delete(sid);
  }

  private _toolLabel(toolName: string): string {
    const labels: Record<string, string> = {
      searchSupportContent: 'Searching PocketLab resources...',
      getProductEntitlements: 'Checking available equipment...',
      searchConversationHistory: 'Checking earlier data...',
      create_visualization: 'Building visualization...',
      set_user_reported_device: 'Saving device...',
      set_standards_framework: 'Saving standards framework...',
      search_lesson_content: 'Searching lesson content...',
      list_document_sections: 'Reading document...',
      get_document_section: 'Reading document...',
      create_text_card: 'Writing card...',
      create_cards_from_document: 'Making cards from the document...',
      create_multiple_choice_card: 'Writing question...',
      create_free_response_card: 'Writing question...',
      create_table_card: 'Building table...',
      create_collect_data_card: 'Setting up data collection...',
      create_matching_card: 'Laying out the activity...',
      update_card: 'Updating card...',
      update_explanation_request: 'Preparing explanation...',
    };
    // Gateway connector tools use the pattern targetName___ToolName
    if (toolName.includes('curriculum')) return 'Checking standards...';
    return labels[toolName] ?? `Running ${toolName}...`;
  }

  private _captureAiGeneration(
    sid: string,
    chunk: APIM.AgentStreamChunk,
    success: boolean,
    errorType?: string,
  ) {
    const user = this._userStore.state();
    const role = UU.isTeacher(user) ? 'teacher' : 'student';
    const toolsUsed = this._toolNamesPerSession.get(sid) ?? [];
    const startTime = this._messageStartTimes.get(sid);
    const latencyMs =
      chunk.latencyMs ?? (startTime ? Date.now() - startTime : undefined);

    // Get conversation content for the trace
    const session = this._memoryStore.getSession(sid);
    const messages = session?.messages ?? [];
    const lastUserMsg = [...messages].reverse().find((m) => m.role === 'user');
    const aiOutput = this._responseTextPerSession.get(sid) ?? '';

    // Include the full system prompt only on the first event per session
    const isFirstCapture = !this._promptCapturedForSession.has(sid);
    const promptProps: Record<string, string> = {};
    if (isFirstCapture && session?.systemPrompt) {
      promptProps.$ai_system_prompt = session.systemPrompt;
      this._promptCapturedForSession.add(sid);
    }

    this._posthog.captureAiEvent(
      '$ai_generation',
      {
        $ai_model: 'claude-sonnet-4-20250514',
        $ai_provider: 'anthropic',
        $ai_input_tokens: chunk.inputTokens,
        $ai_output_tokens: chunk.outputTokens,
        $ai_latency_ms: latencyMs,
        $ai_input: lastUserMsg?.content ?? '',
        $ai_output: aiOutput,
        ...promptProps,
        lessonPK: this._routeStore.state()?.containerId ?? '',
        chatSessionId: sid,
        toolsUsed,
        toolCallCount: toolsUsed.length,
        authenticatedRole: role,
        success,
        ...(errorType ? {errorType} : {}),
      },
      role,
    );

    // Clean up tracking state
    this._toolNamesPerSession.delete(sid);
    this._messageStartTimes.delete(sid);
    this._responseTextPerSession.delete(sid);
  }
}

/**
 * Validates a visualization spec from the AI output.
 * Structural checks only: graphs and svg must exist.
 * Returns an array of error messages. Empty array means valid.
 */
function validateVisualizationSpec(spec: Record<string, unknown>): string[] {
  const errors: string[] = [];

  if (typeof spec['svg'] !== 'string' || !spec['svg'])
    errors.push('Missing SVG markup');

  const graphs = spec['graphs'];
  if (!Array.isArray(graphs) || graphs.length === 0) {
    errors.push('Missing or empty graphs array');
  }

  // SVG size check
  if (typeof spec['svg'] === 'string' && spec['svg'].length > 15000) {
    errors.push('SVG exceeds maximum size (15KB)');
  }

  return errors;
}
